/**
 * adapters/auth/AzimAuthAdapter — implementação de `AuthPort` sobre o IdP
 * self-hosted do serviço authentication (DD-003, ADR-0013).
 *
 * Endpoints consumidos (expostos via gateway YARP em VITE_API_BASE_URL):
 *   POST /auth/token              → emite access_token + refresh_token
 *   POST /auth/token/refresh      → rotaciona o refresh_token (single-use)
 *   POST /auth/invitations/accept → aceite anônimo de convite (define a senha)
 *   POST /auth/password-reset/confirm → confirmação anônima da redefinição de senha
 *
 * Segurança (RNF 1, DD-003):
 * - Access token e refresh token vivem apenas em memória + sessionStorage.
 * - NUNCA em localStorage.
 * - A assinatura RS256 é validada pelo gateway/serviço de authentication; aqui
 *   apenas decodificamos o payload base64url para extrair claims (sub, email, exp).
 *
 * Anti-enumeração (Req 1.4): em signIn, qualquer resposta não-2xx resulta em
 * erro genérico — o adapter nunca revela se o e-mail existe ou não no sistema.
 *
 * requestPasswordReset: delega para data/repos/authRepo.ts, a mesma implementação
 * que SettingsPage já usa contra POST /auth/password-reset, repassando o `tenantSlug` desta
 * instância — sem ele o backend não resolve o tenant. Ver nota inline no método.
 */

import { RefreshTemporarilyUnavailableError } from "./AuthPort.js";
import { generateCorrelationId } from "../http/correlation.js";
import type {
  AuthPort,
  AuthSession,
  InvitationAcceptance,
  InvitationErrorReason,
  PasswordResetConfirmation,
  PasswordResetErrorReason,
  SignInResult,
} from "./AuthPort.js";
import { InvitationError, PasswordResetError } from "./AuthPort.js";
import { requestPasswordReset as repoRequestPasswordReset } from "../../data/repos/authRepo.js";

// ---------------------------------------------------------------------------
// Chaves de sessionStorage (diferentes de "azim.session" do AuthProvider para
// evitar acoplamento; ambas vivem no mesmo storage e são apagadas no signOut).
// ---------------------------------------------------------------------------

const STORAGE_ACCESS_KEY = "azim.auth.access";
const STORAGE_REFRESH_KEY = "azim.auth.refresh";

// ---------------------------------------------------------------------------
// Claims JWT relevantes (snake_case conforme especificado no backend)
// ---------------------------------------------------------------------------

interface JwtClaims {
  sub?: string;
  email?: string;
  /** Expiração em segundos desde epoch (UNIX timestamp). */
  exp?: number;
  tenant_id?: string;
  business_unit_id?: string;
  role?: string;
  memberships?: unknown[];
}

/**
 * Decodifica o payload de um JWT (base64url) sem verificar assinatura.
 * A validação RS256 é responsabilidade do gateway e do serviço authentication.
 *
 * Fix FINDING #3: re-adiciona o padding '=' antes do atob para garantir
 * compatibilidade com browsers que seguem estritamente o spec WHATWG
 * (ex.: Safari < 14) e lançam InvalidCharacterError quando o comprimento
 * do input não é múltiplo de 4.
 *
 * Fix UTF-8: usa TextDecoder para interpretar os bytes decodificados como
 * UTF-8, evitando corrupção de claims com acentos ou caracteres não-ASCII
 * emitidos pelo backend como bytes UTF-8 raw no JSON do payload.
 *
 * Exportada para permitir testes unitários diretos da função pura.
 */
// Reexportado para conveniência de quem já importa do adapter; a definição mora em AuthPort,
// que não importa nada, para não fechar ciclo de módulos.
export { RefreshTemporarilyUnavailableError };

/** Status 4xx que são transitórios por definição — não são recusa de credencial. */
const STATUS_TRANSIENTES = new Set([408, 425, 429]);

/** Janela de espera após uma falha transitória, para não martelar um serviço em dificuldade. */
const COOLDOWN_TRANSIENTE_MS = 5_000;

export function decodeJwtClaims(token: string): JwtClaims {
  try {
    // O `split` ficava FORA do try. Com `token` ausente ou não-string — o que acontece quando a
    // resposta de token vem sem `access_token` —, o TypeError ESCAPAVA desta função, subia por
    // applyTokenResponse e caía no catch de performRefresh, que o rotulava como falha de rede.
    // Um defeito de contrato virava, no log, um problema de conectividade.
    if (typeof token !== "string") return {};
    const parts = token.split(".");
    if (parts.length < 2) return {};
    // base64url → base64 padrão
    let b64 = (parts[1] ?? "").replace(/-/g, "+").replace(/_/g, "/");
    // Re-adiciona padding até comprimento ser múltiplo de 4
    b64 += "=".repeat((4 - (b64.length % 4)) % 4);
    // Decodifica bytes e interpreta como UTF-8 (não latin-1)
    const rawBytes = atob(b64);
    const bytes = new Uint8Array(rawBytes.length);
    for (let i = 0; i < rawBytes.length; i++) {
      bytes[i] = rawBytes.charCodeAt(i);
    }
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json) as JwtClaims;
  } catch {
    return {};
  }
}

/**
 * Retorna true se o access token está expirado ou vai expirar em 30 s.
 * Margem de 30 s evita corrida entre clock do cliente e do servidor.
 *
 * Quando o JWT não tem claim `exp` (token malformado, payload que falhou no
 * decode e retornou {}) tratamos como expirado para forçar refresh/rejeição,
 * evitando que a sessão fique presa num token não-renovável.
 */
function isTokenExpired(token: string): boolean {
  const claims = decodeJwtClaims(token);
  // Token sem `exp` → tratar como expirado (força refresh ou novo login).
  if (claims.exp === undefined) return true;
  const nowSec = Date.now() / 1000;
  return claims.exp - 30 < nowSec;
}

// ---------------------------------------------------------------------------
// Formato de resposta dos endpoints de token
// ---------------------------------------------------------------------------

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
}

/**
 * Resposta da primeira etapa quando o 2FA está habilitado: o backend retorna um
 * desafio (challenge_id + requires_otp) em vez do par de tokens.
 */
interface OtpChallengeResponse {
  challenge_id: string;
  requires_otp: boolean;
}

/** Type guard sobre o corpo da resposta de /auth/token (token vs. desafio OTP). */
function isOtpChallengeBody(
  body: TokenResponse | OtpChallengeResponse,
): body is OtpChallengeResponse {
  return (body as OtpChallengeResponse).requires_otp === true;
}

// ---------------------------------------------------------------------------
// Aceite de convite
// ---------------------------------------------------------------------------

/** Corpo do POST /auth/invitations/accept (snake_case, como o backend espera). */
interface AcceptInvitationRequestBody {
  token: string;
  display_name: string;
  password: string;
}

/** Corpo de sucesso: só o e-mail, para a tela de login preencher. */
interface AcceptInvitationResponseBody {
  email?: unknown;
}

/**
 * Traduz o status HTTP do aceite para a categoria de falha que a UI entende.
 *
 * O mapeamento é deliberadamente estreito: só entra aqui o status cujo significado o backend garante — 410 para token inválido, expirado, revogado ou reusado; 400 e 422 para senha fora da política; 409 para credencial já ativa; 429 e 5xx para indisponibilidade.
 * Todo o resto vira `unknown` em vez de ser chutado para a categoria mais próxima: mandar o convidado pedir um convite novo por causa de um 404 de rota mal publicada seria trocar um problema de deploy por um chamado de suporte.
 */
function invitationReasonForStatus(status: number): InvitationErrorReason {
  if (status === 400 || status === 422) return "weak_password";
  if (status === 410) return "invalid_or_expired";
  if (status === 409) return "conflict";
  if (status === 429 || status >= 500) return "service_unavailable";
  return "unknown";
}

/**
 * Mensagem técnica por categoria — vai para log e depuração, não para a tela.
 * A cópia que o convidado lê é escolhida pela página a partir de `reason`.
 * Nenhuma mensagem repete token, senha ou e-mail.
 */
const INVITATION_ERROR_MESSAGES: Readonly<Record<InvitationErrorReason, string>> = {
  invalid_or_expired: "Convite inválido, expirado, revogado ou já utilizado.",
  weak_password: "Senha recusada pela política do servidor.",
  conflict: "Já existe credencial ativa para este convite.",
  service_unavailable: "Serviço de aceite de convite indisponível.",
  network: "Falha de rede ao enviar o aceite do convite.",
  unknown: "Falha não classificada no aceite do convite.",
};

// ---------------------------------------------------------------------------
// Confirmação de redefinição de senha
// ---------------------------------------------------------------------------

/** Corpo do POST /auth/password-reset/confirm (snake_case, como o backend espera). */
interface ConfirmPasswordResetRequestBody {
  token: string;
  password: string;
}

/** Corpo de sucesso: só o e-mail, para a tela de login preencher. */
interface ConfirmPasswordResetResponseBody {
  email?: unknown;
}

/**
 * Traduz o status HTTP da confirmação para a categoria de falha que a UI entende.
 *
 * O mapeamento espelha o que o backend garante: 410 (AUTH-ERR-036) para token inválido, expirado, de outro tenant, sem conta ativa ou já utilizado; 400 e 422 (AUTH-ERR-034) para senha fora da política; 429 e 5xx para indisponibilidade.
 * Todo o resto vira `unknown` em vez de ser chutado para a categoria mais próxima: mandar a pessoa pedir outro link por causa de um 404 de rota mal publicada trocaria um problema de deploy por um chamado de suporte.
 */
function passwordResetReasonForStatus(status: number): PasswordResetErrorReason {
  if (status === 400 || status === 422) return "weak_password";
  if (status === 410) return "invalid_or_expired";
  if (status === 429 || status >= 500) return "service_unavailable";
  return "unknown";
}

/**
 * Mensagem técnica por categoria — vai para log e depuração, não para a tela.
 * A cópia que o usuário lê é escolhida pela página a partir de `reason`.
 * Nenhuma mensagem repete token, senha ou e-mail.
 */
const PASSWORD_RESET_ERROR_MESSAGES: Readonly<Record<PasswordResetErrorReason, string>> = {
  invalid_or_expired: "Link de redefinição inválido, expirado ou já utilizado.",
  weak_password: "Senha recusada pela política do servidor.",
  service_unavailable: "Serviço de redefinição de senha indisponível.",
  network: "Falha de rede ao enviar a redefinição de senha.",
  unknown: "Falha não classificada na redefinição de senha.",
};

// ---------------------------------------------------------------------------
// Opções do construtor
// ---------------------------------------------------------------------------

export interface AzimAuthAdapterOptions {
  /**
   * URL base da API (VITE_API_BASE_URL), ex.: "http://localhost:8080/v1".
   * Os endpoints de auth serão: `${baseUrl}/auth/token` e
   * `${baseUrl}/auth/token/refresh`.
   */
  baseUrl: string;
  /**
   * Slug do tenant (header `X-Tenant-Slug`), exigido pelo IdP self-hosted para
   * resolver o tenant no login/refresh (ADR-0017). Default: "azim".
   */
  tenantSlug?: string;
  /** fetch injetável (default: global fetch) — facilita testes unitários. */
  fetchImpl?: typeof fetch;
}

// ---------------------------------------------------------------------------
// AzimAuthAdapter
// ---------------------------------------------------------------------------

export class AzimAuthAdapter implements AuthPort {
  private readonly baseUrl: string;
  private readonly tenantSlug: string;
  private readonly fetchImpl: typeof fetch;

  /** Sessão corrente em memória (uid, email, access_token). */
  private currentSession: AuthSession | null = null;
  /** Refresh token (single-use; rotacionado a cada /auth/token/refresh). */
  private refreshToken: string | null = null;
  /**
   * Refresh em voo compartilhado (coalescing). Como o refresh token é single-use
   * e o backend revoga toda a família ao detectar reuso, N chamadas concorrentes
   * que tomam 401 ao mesmo tempo NÃO podem disparar N refreshes com o mesmo token
   * (a 2ª+ chegaria com o token já rotacionado → revogação da família → logout).
   * Todas compartilham esta mesma Promise; limpa no finally.
   */
  private refreshInFlight: Promise<string | null> | null = null;
  /** Instante (epoch ms) até o qual novas tentativas de renovação são recusadas de imediato. */
  private transienteAte = 0;
  /** Conjunto de observadores de estado de autenticação. */
  private readonly listeners = new Set<(session: AuthSession | null) => void>();

  constructor(options: AzimAuthAdapterOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.tenantSlug = options.tenantSlug?.trim() || "azim";
    // O fetch global DEVE ser chamado com `this === window`. Guardá-lo direto em
    // `this.fetchImpl` e invocá-lo como método (`this.fetchImpl(...)`) lança
    // "TypeError: Illegal invocation" antes de enviar a requisição. Bind ao escopo global.
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    // Restaura tokens persistidos: permite reload da aba sem novo login
    this.hydrateFromStorage();
  }

  // -------------------------------------------------------------------------
  // Gerenciamento de storage
  // -------------------------------------------------------------------------

  /** Restaura tokens do sessionStorage após reload da página. */
  private hydrateFromStorage(): void {
    try {
      const storedAccess = sessionStorage.getItem(STORAGE_ACCESS_KEY);
      const storedRefresh = sessionStorage.getItem(STORAGE_REFRESH_KEY);
      if (!storedAccess || !storedRefresh) return;
      const claims = decodeJwtClaims(storedAccess);
      if (!claims.sub) return;
      this.currentSession = {
        uid: claims.sub,
        email: claims.email ?? "",
        token: storedAccess,
      };
      this.refreshToken = storedRefresh;
    } catch {
      // sessionStorage indisponível (CSP, private browsing) → sessão limpa
    }
  }

  /** Persiste tokens no sessionStorage (expira com o fechamento da aba). */
  private persistToStorage(accessToken: string, refreshToken: string): void {
    try {
      sessionStorage.setItem(STORAGE_ACCESS_KEY, accessToken);
      sessionStorage.setItem(STORAGE_REFRESH_KEY, refreshToken);
    } catch {
      // Quota excedida ou storage indisponível — tokens ficam apenas em memória
    }
  }

  /** Remove tokens do sessionStorage. */
  private clearStorage(): void {
    try {
      sessionStorage.removeItem(STORAGE_ACCESS_KEY);
      sessionStorage.removeItem(STORAGE_REFRESH_KEY);
    } catch {
      // ignorado
    }
  }

  // -------------------------------------------------------------------------
  // Observadores
  // -------------------------------------------------------------------------

  /**
   * Notifica todos os observadores registrados com a sessão fornecida.
   *
   * Cada observador é isolado. Sem isso, um único que lançasse abortava o laço, subia por
   * applyTokenResponse e derrubava uma renovação que o SERVIDOR já tinha concluído — e como o
   * refresh token é single-use e já fora rotacionado, não havia volta: o usuário perdia a sessão
   * com um token novo e válido em memória. Um defeito na UI não pode invalidar credencial.
   */
  private notify(session: AuthSession | null): void {
    for (const cb of this.listeners) {
      try {
        cb(session);
      } catch (erro) {
        // Loga e segue: os demais observadores precisam receber o evento.
        console.error("[auth] observador de estado de sessão lançou; renovação preservada", erro);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Processamento de resposta de token
  // -------------------------------------------------------------------------

  /**
   * Aplica a resposta de token (emitido ou renovado): atualiza estado em
   * memória, sessionStorage e notifica todos os observadores.
   */
  private applyTokenResponse(data: TokenResponse): AuthSession {
    // Um `access_token` ausente, vazio ou não-string é resposta INVÁLIDA, e precisa ser recusada
    // aqui, com nome. Sem esta guarda, a string vazia virava uma sessão cujo token é "" — que o
    // ApiClient lê como falsy e trata como sessão morta, sem nada ter lançado e sem nada no log.
    if (typeof data?.access_token !== "string" || data.access_token.length === 0) {
      throw new Error("Resposta de token sem access_token utilizável.");
    }

    // O refresh_token também precisa vir. Sem ele, a renovação DESTA vez funciona e a SEGUINTE
    // cai no ramo "sem refresh token" de getIdToken, que declara a sessão nula sem limpar o
    // storage — degradação silenciosa e adiada, da mesma família que este conserto elimina.
    if (typeof data.refresh_token !== "string" || data.refresh_token.length === 0) {
      throw new Error("Resposta de token sem refresh_token utilizável.");
    }

    const claims = decodeJwtClaims(data.access_token);
    const session: AuthSession = {
      uid: claims.sub ?? "",
      email: claims.email ?? "",
      token: data.access_token,
    };
    this.currentSession = session;
    this.refreshToken = data.refresh_token;
    this.persistToStorage(data.access_token, data.refresh_token);
    this.notify(session);
    return session;
  }

  // -------------------------------------------------------------------------
  // AuthPort
  // -------------------------------------------------------------------------

  /**
   * Primeira etapa do login via POST /auth/token.
   *
   * Retorna:
   * - `AuthSession` quando o 2FA está desabilitado (o backend devolve o par de tokens), ou
   * - `OtpChallenge` quando o 2FA está habilitado (o backend devolve challenge_id +
   *   requires_otp e envia o código por e-mail).
   *
   * Qualquer resposta não-2xx lança erro genérico (anti-enumeração, Req 1.4):
   * a mensagem nunca revela se o e-mail existe no sistema.
   */
  async signIn(email: string, password: string): Promise<SignInResult> {
    const res = await this.fetchImpl(`${this.baseUrl}/auth/token`, {
      method: "POST",
      headers: {
          "Content-Type": "application/json",
          "X-Tenant-Slug": this.tenantSlug,
          "X-Correlation-Id": generateCorrelationId(),
        },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      // Anti-enumeração: mensagem genérica independente do status HTTP real
      throw new Error("Credenciais inválidas. Verifique e tente novamente.");
    }

    const data = (await res.json()) as TokenResponse | OtpChallengeResponse;

    // 2FA habilitado: a 1ª etapa retorna um desafio (sem JWT).
    if (isOtpChallengeBody(data)) {
      return { kind: "otp_challenge", challengeId: data.challenge_id };
    }

    return this.applyTokenResponse(data);
  }

  /**
   * Segunda etapa do login (2FA): verifica o código OTP do desafio via
   * POST /auth/2fa/verify e, em caso de sucesso, aplica a sessão (igual ao signIn direto).
   *
   * Mapeia os erros do backend para mensagens pt-BR:
   * - 429 (lockout) → "Muitas tentativas. Solicite um novo código."
   * - demais não-2xx (código inválido/expirado) → "Código inválido ou expirado."
   */
  async verifyOtp(challengeId: string, code: string): Promise<AuthSession> {
    const res = await this.fetchImpl(`${this.baseUrl}/auth/2fa/verify`, {
      method: "POST",
      headers: {
          "Content-Type": "application/json",
          "X-Tenant-Slug": this.tenantSlug,
          "X-Correlation-Id": generateCorrelationId(),
        },
      body: JSON.stringify({ challenge_id: challengeId, code }),
    });

    if (!res.ok) {
      if (res.status === 429) {
        throw new Error("Muitas tentativas. Solicite um novo código.");
      }
      throw new Error("Código inválido ou expirado.");
    }

    const data = (await res.json()) as TokenResponse;
    return this.applyTokenResponse(data);
  }

  /**
   * Retorna o access token corrente:
   * - Sem sessão → null.
   * - Token expirado ou `forceRefresh` → chama /auth/token/refresh e rotaciona.
   * - Token válido → retorna diretamente sem chamada de rede.
   */
  async getIdToken(forceRefresh = false): Promise<string | null> {
    if (!this.currentSession) return null;

    const needsRefresh = forceRefresh || isTokenExpired(this.currentSession.token);

    if (!needsRefresh) {
      return this.currentSession.token;
    }

    // Sem refresh token → sessão irreversivelmente expirada
    if (!this.refreshToken) {
      this.notify(null);
      return null;
    }

    // Coalescing: se já há um refresh em voo, todas as chamadas concorrentes
    // aguardam a MESMA Promise (evita disparar múltiplos POST /auth/token/refresh
    // com o mesmo refresh token single-use, que derrubaria a sessão inteira).
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  /**
   * Executa a rotação do refresh token uma única vez. Chamado exclusivamente
   * através de <c>getIdToken</c> sob o guarda de <c>refreshInFlight</c>.
   */
  private async performRefresh(): Promise<string | null> {
    // Disjuntor. Ao parar de deslogar em falha transitória, tirei o freio que existia por
    // acidente: antes, o primeiro não-2xx encerrava a sessão e com ela a rajada. Sem substituto,
    // cada 401 voltaria a disparar um refresh — e com N telas, N abas e as retentativas do
    // react-query, o portal martelaria um serviço já em dificuldade até estourar as 200
    // requisições por 10 segundos do gateway, cujo 429 é justamente o que não pode deslogar.
    // O coalescing do refreshInFlight só junta chamadas SIMULTÂNEAS; não freia as sequenciais.
    if (Date.now() < this.transienteAte) {
      throw new RefreshTemporarilyUnavailableError(
        "Renovação em espera após falha recente do servidor de autenticação.",
      );
    }

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}/auth/token/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-Slug": this.tenantSlug,
          "X-Correlation-Id": generateCorrelationId(),
        },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });
    } catch (erro) {
      // Rede fora. A sessão continua viva; não se toca em token nenhum.
      this.transienteAte = Date.now() + COOLDOWN_TRANSIENTE_MS;
      throw new RefreshTemporarilyUnavailableError(
        "Não foi possível falar com o servidor de autenticação.",
        { cause: erro },
      );
    }

    if (!res.ok) {
      // A fronteira NÃO é 5xx. 408, 425 e 429 são 4xx transitórios por definição, e o 429 é
      // alcançável neste caminho exato: o gateway impõe 200 requisições por 10 segundos em
      // /v1/auth/**, particionado por IP. Deixá-lo do lado definitivo faria o rate limit deslogar
      // o usuário — o mesmo desfecho que este conserto existe para eliminar.
      if (STATUS_TRANSIENTES.has(res.status) || res.status >= 500) {
        this.transienteAte = Date.now() + COOLDOWN_TRANSIENTE_MS;
        throw new RefreshTemporarilyUnavailableError(
          `Servidor de autenticação indisponível (HTTP ${res.status}).`,
        );
      }

      // 4xx: refresh inválido, expirado ou revogado. É definitivo — insistir dispara a detecção
      // de reuso no servidor, que revoga a família inteira de tokens.
      await this.signOut();
      return null;
    }

    // Daqui para baixo o servidor JÁ rotacionou o refresh token: o anterior está revogado. Um
    // acidente aqui é fatal para a sessão, e por isso precisa ser RUIDOSO — era exatamente este
    // trecho que o catch anterior rotulava como "falha de rede", escondendo a causa real.
    try {
      const data = (await res.json()) as TokenResponse;
      const session = this.applyTokenResponse(data);
      this.transienteAte = 0;
      return session.token;
    } catch (erro) {
      console.error(
        "[auth] renovação respondeu 200 mas a resposta não pôde ser aplicada; " +
          "o refresh token anterior já foi revogado pelo servidor",
        erro,
      );
      await this.signOut();
      return null;
    }
  }

  /**
   * Limpa tokens de memória e sessionStorage, notifica observadores com null.
   * Não realiza chamada de rede (logout é puramente local no frontend;
   * o token expira naturalmente ou pode ser revogado via backend).
   */
  async signOut(): Promise<void> {
    this.currentSession = null;
    this.refreshToken = null;
    this.clearStorage();
    this.notify(null);
  }

  /**
   * Aceite de convite via POST /auth/invitations/accept (rota anônima).
   *
   * O `X-Tenant-Slug` é obrigatório: é por ele que o authentication resolve o tenant do convidado, e nos hosts por cliente o gateway já o sobrescreve a partir do host — o mesmo caminho que o login usa.
   *
   * Não persiste nada em sessionStorage e não notifica observadores: o aceite grava a credencial no servidor, mas quem autentica é o login, com segundo fator.
   * Um sucesso aqui deixa o portal exatamente tão anônimo quanto estava antes.
   */
  async acceptInvitation(
    token: string,
    displayName: string,
    password: string,
  ): Promise<InvitationAcceptance> {
    const body: AcceptInvitationRequestBody = {
      token,
      display_name: displayName,
      password,
    };

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}/auth/invitations/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-Slug": this.tenantSlug,
          "X-Correlation-Id": generateCorrelationId(),
        },
        body: JSON.stringify(body),
      });
    } catch {
      // Sem resposta HTTP: rede, DNS, CORS ou aba offline.
      // É distinto de indisponibilidade do serviço, ainda que a tela ofereça repetição nos dois casos.
      throw new InvitationError("network", INVITATION_ERROR_MESSAGES.network);
    }

    if (!res.ok) {
      const reason = invitationReasonForStatus(res.status);
      throw new InvitationError(reason, INVITATION_ERROR_MESSAGES[reason]);
    }

    // A credencial já foi gravada neste ponto.
    // Um corpo ausente ou malformado não pode virar erro: falharia uma operação que deu certo e empurraria o convidado a repetir o que já está feito.
    // Sem e-mail, a tela de login apenas não vem preenchida.
    let data: AcceptInvitationResponseBody;
    try {
      data = (await res.json()) as AcceptInvitationResponseBody;
    } catch {
      data = {};
    }

    return { email: typeof data.email === "string" ? data.email : "" };
  }

  /**
   * Reset de senha via POST /v1/auth/password-reset (Req 3.2).
   *
   * Delega para data/repos/authRepo.ts — a mesma implementação que SettingsPage
   * já usa — passando o `baseUrl`, o `fetchImpl` e o `tenantSlug` desta instância, em vez de
   * duplicar o fetch aqui: uma única implementação, dois chamadores.
   *
   * O `tenantSlug` não é opcional na prática: o `TenantResolutionMiddleware` do authentication
   * resolve o tenant por ele, e sem o header o `PasswordResetController` cai no ramo de erro e
   * devolve 500 AUTH-ERR-090. Foi o que aconteceu em produção enquanto este método delegava sem
   * repassar o slug — as outras quatro rotas de auth deste mesmo adapter sempre o enviaram.
   *
   * O header identifica o TENANT, não o usuário, então não abre brecha de enumeração: o servidor
   * responde 202 igual para e-mail existente e inexistente (PBT-03). O AuthProvider suprime o
   * erro deste método na tela de login, de modo que nem sucesso nem falha distinguem os dois
   * casos para quem está lá.
   */
  async requestPasswordReset(email: string): Promise<void> {
    await repoRequestPasswordReset(email, {
      baseUrl: this.baseUrl,
      fetchImpl: this.fetchImpl,
      tenantSlug: this.tenantSlug,
    });
  }

  /**
   * Confirmação da redefinição via POST /auth/password-reset/confirm (rota anônima).
   *
   * É a segunda metade do fluxo, e sem ela a primeira não serve para nada: o e-mail chegava com um link para `/auth/reset-password?token=…`, o portal não tinha a rota, o token era descartado no caminho para o dashboard e ninguém conseguia trocar a senha.
   *
   * O `X-Tenant-Slug` é obrigatório aqui pela mesma razão do pedido: é por ele que o authentication resolve o tenant, e nos hosts por cliente o gateway já o sobrescreve a partir do host.
   *
   * Não persiste nada em sessionStorage e não notifica observadores: a confirmação grava a senha no servidor, mas quem autentica é o login, com segundo fator.
   * Um sucesso aqui deixa o portal exatamente tão anônimo quanto estava antes.
   */
  async confirmPasswordReset(token: string, password: string): Promise<PasswordResetConfirmation> {
    const body: ConfirmPasswordResetRequestBody = { token, password };

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}/auth/password-reset/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-Slug": this.tenantSlug,
          "X-Correlation-Id": generateCorrelationId(),
        },
        body: JSON.stringify(body),
      });
    } catch {
      // Sem resposta HTTP: rede, DNS, CORS ou aba offline.
      throw new PasswordResetError("network", PASSWORD_RESET_ERROR_MESSAGES.network);
    }

    if (!res.ok) {
      const reason = passwordResetReasonForStatus(res.status);
      throw new PasswordResetError(reason, PASSWORD_RESET_ERROR_MESSAGES[reason]);
    }

    // A senha já foi gravada neste ponto.
    // Um corpo ausente ou malformado não pode virar erro: falharia uma operação que deu certo e empurraria a pessoa a repetir com um token que o servidor já consumiu — e a repetição seria recusada.
    // Sem e-mail, a tela de login apenas não vem preenchida.
    let data: ConfirmPasswordResetResponseBody;
    try {
      data = (await res.json()) as ConfirmPasswordResetResponseBody;
    } catch {
      data = {};
    }

    return { email: typeof data.email === "string" ? data.email : "" };
  }

  /**
   * Registra um observador de estado de autenticação.
   *
   * Semântica Firebase-like: emite o estado corrente imediatamente ao registrar
   * (síncrono). Notifica novamente a cada signIn, signOut e refresh de token.
   *
   * @returns Função de unsubscribe — remova o observador quando não for mais necessário.
   */
  onAuthStateChanged(callback: (session: AuthSession | null) => void): () => void {
    this.listeners.add(callback);
    // Emite estado corrente ao registrar (síncrono, sem await). Isolado pela mesma razão do
    // notify: um observador com defeito não pode derrubar quem o registrou.
    try {
      callback(this.currentSession);
    } catch (erro) {
      console.error("[auth] observador lançou ao receber o estado inicial", erro);
    }
    return () => {
      this.listeners.delete(callback);
    };
  }
}

// ---------------------------------------------------------------------------
// Fábrica
// ---------------------------------------------------------------------------

export interface AzimAuthEnv {
  /** `import.meta.env.VITE_API_BASE_URL` */
  apiBaseUrl: string | undefined;
  /**
   * `import.meta.env.VITE_AUTH_TENANT_SLUG` — slug do tenant no header `X-Tenant-Slug`
   * (ADR-0017). Ausente ou em branco, o adapter cai no default "azim".
   */
  authTenantSlug?: string | undefined;
}

/**
 * Cria um `AzimAuthAdapter` a partir das variáveis de ambiente.
 * Usa "/v1" como base URL quando VITE_API_BASE_URL não está definido.
 *
 * O slug do tenant precisa atravessar esta fábrica: sem ele todo adapter construído aqui falaria
 * com o tenant "azim", independentemente do cliente configurado no ambiente — o
 * `TenantResolutionMiddleware` resolveria o tenant errado, e o login de um cliente cairia na base
 * de outro ou simplesmente não encontraria a conta. Hoje o defeito está inerte porque o `main.tsx`
 * constrói o adapter diretamente; ele acordaria no primeiro uso da fábrica em produção.
 */
export function createAzimAuthAdapter(
  env: AzimAuthEnv,
  fetchImpl?: typeof fetch,
): AzimAuthAdapter {
  const baseUrl = (env.apiBaseUrl ?? "/v1").trim();
  const options: AzimAuthAdapterOptions = { baseUrl };

  // A normalização de branco e o default "azim" moram no construtor do adapter; repassar o valor
  // cru mantém uma única definição da regra.
  if (env.authTenantSlug !== undefined) options.tenantSlug = env.authTenantSlug;
  if (fetchImpl) options.fetchImpl = fetchImpl;

  return new AzimAuthAdapter(options);
}
